import { Body1, Headline2, Headline3 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled, { keyframes } from 'styled-components'
import SmallFoxAsset from 'assets/homepage-redesign/wink.svg'
import NextImage from 'components/elements/NextImage'
import Line from 'assets/homepage-redesign/line.png'
import _useTranslation from 'lib/hooks/_useTranslation'
import Stack from 'components/elements/Stack'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    background: radial-gradient(52.34% 100% at 50% 0%, #6c3ebc 0%, #0d0d0d 100%);
    padding: 0px 0 10px;
    position: relative;
    overflow: hidden;
    min-height: 400px;
    width: 100%;
    @media (max-width: 768px) {
        max-width: 100%;
        min-height: 300px;
        background: radial-gradient(164.44% 100% at 50% 0%, #6c3ebc 0%, #0d0d0d 75%);
    }
`

const MessagesContainer = styled.div`
    position: relative;
    width: 100%;
    height: 300px;
    margin: 10px 0 20px;
    max-width: 1100px;
    padding: 0;
    @media (max-width: 740px) {
        height: 465px;
        margin: 0px;
        position: relative;
    }
`

const float = keyframes`
    0% { transform: translate(0, 0); }
    25% { transform: translate(8px, -8px); }
    50% { transform: translate(0, -12px); }
    75% { transform: translate(-8px, -4px); }
    100% { transform: translate(0, 0); }
`

const mobileFloat = keyframes`
    0% { transform: translate(0, 0); }
    25% { transform: translate(4px, -4px); }
    50% { transform: translate(0, -8px); }
    75% { transform: translate(-4px, -2px); }
    100% { transform: translate(0, 0); }
`

interface MessageProps {
    backgroundColor: string
    zIndex: number
    position: string
    mobilePosition?: string
    animationDelay: string
    animationDuration: string
    padding: string
    borderRadius: string
    width: string
    fontSize: number
    mobileFontSize: number
    mobilePadding: string
    mobileBorderRadius: string
    mobileWidth: string
    lineHeight: number
    mobileLineHeight: number
    letterSpacing: number
    mobileLetterSpacing: number
    fontWeight: number
    mobileFontWeight: number
}

const Message = styled.div<MessageProps>`
    position: absolute;
    padding: ${(props) => props.padding};
    border-radius: ${(props) => props.borderRadius};
    background-color: ${(props) => props.backgroundColor};
    border: 1px solid #ffffff1a;
    max-width: ${(props) => props.width};
    white-space: normal;
    border: 1px solid #ffffff1a;
    z-index: ${(props) => props.zIndex};
    ${(props) => props.position};
    animation: ${float} ${(props) => props.animationDuration} ease-in-out infinite;
    animation-delay: ${(props) => props.animationDelay};
    box-shadow: 0px 10px 48px 0px #00000066;

    p {
        line-height: ${(props) => props.lineHeight}px;
        letter-spacing: ${(props) => props.letterSpacing}px;
        font-weight: ${(props) => props.fontWeight};
    }

    &:hover {
        z-index: 20;
    }
    @media (max-width: 740px) {
        animation: ${mobileFloat} 10s ease-in-out infinite;
        max-width: ${(props) => props.mobileWidth};
        padding: 12px 16px;
        box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.3);
        ${(props) => props.mobilePosition && props.mobilePosition};
        border-radius: ${(props) => props.mobileBorderRadius};
        padding: ${(props) => props.mobilePadding};
        p {
            font-weight: ${(props) => props.mobileFontWeight};
            line-height: ${(props) => props.mobileLineHeight}px;
            letter-spacing: ${(props) => props.mobileLetterSpacing}px;
            font-size: ${(props) => props.mobileFontSize};
        }
    }

    @media (-webkit-min-device-pixel-ratio: 2) and (max-width: 1024px) {
        background-color: ${(props) => props.backgroundColor};
    }
`

const StyledLine = styled.div`
    width: 100%;
    max-width: 400px;
    display: flex;
    justify-content: center;
    position: relative;
    right: 5px;
    bottom: -10px;
    padding-top: 40px;
    transform: translateZ(0);
    @media (max-width: 740px) {
        padding-top: 10px;
        img {
            width: 94px;
            height: 94px;
        }
    }
`

const FoxImage = styled.div`
    top: 0px;
    scale: 0.8;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
`

const ContentWrapper = styled.div`
    max-width: 600px;
    margin-bottom: 10px;
    text-align: center;
    padding: 0 15px;
    h2 {
        font-family: var(--font-inter);
        font-weight: 650;
        font-size: 36px;
        line-height: 44px;
        letter-spacing: -0.8px;
    }
    h3 {
        font-family: var(--font-inter);
        font-weight: 650;
        font-size: 23px;
        line-height: 28px;
        letter-spacing: -0.2px;
        color: ${theme.colors.grayScaleSecondary};
    }
    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding-bottom: 20px;
        h2 {
            font-weight: 650;
            font-size: 30px;
            line-height: 1.2;
            letter-spacing: -0.4px;
            line-height: 36px;
        }
        h3 {
            font-weight: 650 !important;
            font-size: 20px;
            line-height: 24px;
            letter-spacing: 0px;
        }
    }
`

const HomepageAICompanion = () => {
    const { t } = _useTranslation('homepage')

    const messageData = [
        {
            id: 1,
            backgroundColor: '#5767D6',
            text: t('homepage/aiCompanionMessage1'),
            position: 'left: 5%; top: 15%;',
            mobilePosition: 'right: 3%; top: 0%;',
            fontSize: 17,
            lineHeight: 20,
            letterSpacing: 0.3,
            fontWeight: 550,
            mobileFontSize: 13,
            mobileLineHeight: 20,
            mobileLetterSpacing: 0.4,
            mobileFontWeight: 550,
            duration: '13s',
            delay: '0s',
            zIndex: 3,
            padding: '16px 20px',
            borderRadius: '20px 20px 4px 20px',
            mobilePadding: '8px 12px',
            mobileBorderRadius: '16px 16px 4px 16px',
            width: '260px',
            mobileWidth: '240px',
        },
        {
            id: 2,
            backgroundColor: '#008DE8',
            text: t('homepage/aiCompanionMessage2'),
            position: 'right: 40%; top: 30%;',
            mobilePosition: 'right: 5%; top: 15%;',
            duration: '17s',
            fontSize: 23,
            lineHeight: 28,
            letterSpacing: -0.2,
            fontWeight: 550,
            mobileFontSize: 20,
            mobileLineHeight: 24,
            mobileLetterSpacing: -0.2,
            mobileFontWeight: 500,
            delay: '-2s',
            zIndex: 8,
            padding: '28px 28px',
            borderRadius: '28px 28px 4px 28px',
            mobilePadding: '12px 16px',
            mobileBorderRadius: '20px 20px 4px 20px',
            width: '320px',
            mobileWidth: '280px',
        },
        {
            id: 3,
            backgroundColor: '#5767D6',
            text: t('homepage/aiCompanionMessage3'),
            position: 'right: 10%; top: 15%;',
            mobilePosition: 'left: 5%; top: 72.5%;',
            duration: '14s',
            fontSize: 17,
            lineHeight: 20,
            letterSpacing: 0.3,
            fontWeight: 550,
            mobileFontSize: 13,
            mobileLineHeight: 20,
            mobileLetterSpacing: 0.4,
            mobileFontWeight: 550,
            delay: '-4s',
            zIndex: 4,
            padding: '16px 20px',
            borderRadius: '20px 20px 4px 20px',
            mobilePadding: '8px 12px',
            mobileBorderRadius: '16px 16px 4px 16px',
            width: '268px',
            mobileWidth: '170px',
        },
        {
            id: 4,
            backgroundColor: '#5767D6',
            text: t('homepage/aiCompanionMessage4'),
            position: 'left: 10%; top: 75%;',
            mobilePosition: 'left: 42.5%; top: 87.5%;',
            duration: '15s',
            fontSize: 17,
            lineHeight: 20,
            letterSpacing: 0.3,
            fontWeight: 550,
            mobileFontSize: 13,
            mobileLineHeight: 20,
            mobileLetterSpacing: 0.4,
            mobileFontWeight: 500,
            delay: '-6s',
            zIndex: 10,
            padding: '16px 20px',
            borderRadius: '20px 20px 4px 20px',
            mobilePadding: '8px 12px',
            mobileBorderRadius: '16px 16px 4px 16px',
            width: '278px',
            mobileWidth: '200px',
        },
        {
            id: 5,
            backgroundColor: '#845BC6',
            text: t('homepage/aiCompanionMessage5'),
            position: 'left: 45%; top: 85%;',
            mobilePosition: 'left: 50%; top: 60%;',
            duration: '16s',
            delay: '-3s',
            fontSize: 20,
            lineHeight: 24,
            letterSpacing: 0.3,
            fontWeight: 550,
            mobileFontSize: 17,
            mobileLineHeight: 20,
            mobileLetterSpacing: 0,
            mobileFontWeight: 500,
            zIndex: 7,
            padding: '20px 24px',
            borderRadius: '24px 24px 4px 24px',
            mobilePadding: '12px 16px',
            mobileBorderRadius: '20px 20px 4px 20px',
            width: '250px',
            mobileWidth: '205px',
        },
        {
            id: 6,
            backgroundColor: '#50B354',
            text: t('homepage/aiCompanionMessage6'),
            position: 'right: 10%; top: 55%;',
            mobilePosition: 'left: 3%; top: 37.5%;',
            duration: '18s',
            fontSize: 20,
            lineHeight: 24,
            letterSpacing: 0.3,
            fontWeight: 550,
            mobileFontSize: 17,
            mobileLineHeight: 20,
            mobileLetterSpacing: 0,
            mobileFontWeight: 550,
            delay: '-5s',
            zIndex: 6,
            padding: '20px 24px',
            borderRadius: '24px 24px 4px 24px',
            mobilePadding: '12px 16px',
            mobileBorderRadius: '20px 20px 4px 20px',
            width: '240px',
            mobileWidth: '230px',
        },
    ]

    return (
        <Container>
            <Stack gutter={0}>
                <FoxImage>
                    <SmallFoxAsset />
                </FoxImage>
                <ContentWrapper>
                    <Headline2
                        textAlign="center"
                        color={theme.colors.white}
                        fontSize={36}
                        fontFamily="var(--font-inter)"
                        fontWeight={550}
                    >
                        {t('homepage/aiCompanionTitle')}
                    </Headline2>
                    <Headline3
                        textAlign="center"
                        color={theme.colors.grayScaleSecondary}
                        fontSize={22}
                        fontFamily="var(--font-inter)"
                        fontWeight={550}
                    >
                        {t('homepage/aiCompanionSubtitle')}
                    </Headline3>
                </ContentWrapper>
            </Stack>
            <MessagesContainer>
                {messageData.map((message) => (
                    <Message
                        key={message.id}
                        backgroundColor={message.backgroundColor}
                        zIndex={message.zIndex}
                        position={message.position}
                        mobilePosition={message.mobilePosition}
                        animationDuration={message.duration}
                        animationDelay={message.delay}
                        padding={message.padding}
                        borderRadius={message.borderRadius}
                        width={message.width}
                        fontSize={message.fontSize}
                        mobileFontSize={message.mobileFontSize}
                        mobilePadding={message.mobilePadding}
                        mobileBorderRadius={message.mobileBorderRadius}
                        mobileWidth={message.mobileWidth}
                        lineHeight={message.lineHeight}
                        mobileLineHeight={message.mobileLineHeight}
                        letterSpacing={message.letterSpacing}
                        mobileLetterSpacing={message.mobileLetterSpacing}
                        fontWeight={message.fontWeight}
                        mobileFontWeight={message.mobileFontWeight}
                    >
                        <Body1
                            color={'#F9F9F9'}
                            fontFamily="var(--font-inter)"
                            fontWeight={400}
                            fontSize={message.fontSize ? message.fontSize : 17}
                        >
                            {message.text}
                        </Body1>
                    </Message>
                ))}
            </MessagesContainer>

            {/* <StyledSubtitle
                textAlign="center"
                color={theme.colors.white}
                fontSize={34}
                fontFamily="var(--font-inter)"
                fontWeight={550}
            >
                {t('homepage/aiCompanionSubtitle2')}
            </StyledSubtitle> */}

            <StyledLine>
                <NextImage src={Line.src} alt="Line" width={130} height={130} />
            </StyledLine>
        </Container>
    )
}

export default HomepageAICompanion
